import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import type { Group } from "@models/ISchema";
import {
  useApprovalFlowRoles,
  useLoadApprovalFlowAbilities,
} from "@services/collections/assignment-proposal/assignmentProposalServices";
import { useProposalIndicator } from "@services/collections/assignment-proposal/assignmentProposalListServices";
import type { ProposalEditorLocationState } from "@services/types/assignment-proposal/assignmentProposalTypes";
import { resolveProgrammingGroupState } from "@utils/programmingGroupState";

/**
 * The assignment proposals of the group calendar: the header access, with the reviewer's dot, and
 * the props of the Drawer. Proposals belong to the group's links; only a requester or a reviewer
 * gets them, and a group with microprogramming pending or applied does not admit them.
 */
export const useGroupProposals = (group: Group | null | undefined) => {
  const { origin, scenario } = useContextUrl();
  const history = useHistory();
  const { editAssignmentUrl } = useNavigationUrl();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useLoadApprovalFlowAbilities();
  const { canSubmit, canReview } = useApprovalFlowRoles();

  const links = useMemo(
    () =>
      (group?.links ?? [])
        .filter(link => !!link?.id)
        .map(link => ({ id: String(link.id), code: link.code })),
    [group?.links],
  );
  const linkIds = useMemo(() => links.map(link => link.id), [links]);
  const acceptsProposals = links.length > 0 && !resolveProgrammingGroupState(group?.labels);
  const canSeeProposals = acceptsProposals && (canSubmit || canReview);

  const { hasIndicator, linkToReview, setHasPending } = useProposalIndicator({
    scenarioId: scenario,
    originId: origin,
    linkIds,
    isEnabled: acceptsProposals && canReview,
  });

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const access = useMemo(
    () => (canSeeProposals ? { hasIndicator, onOpen: openDrawer } : null),
    [canSeeProposals, hasIndicator, openDrawer],
  );

  const openProposal = (linkId: string, proposalId: string, options: { review: boolean }) => {
    // The editor comes back to this calendar after a decision on the proposal.
    const editorState: ProposalEditorLocationState = {
      returnTo: `${history.location.pathname}${history.location.search}`,
    };
    history.push(editAssignmentUrl(linkId, proposalId, options), editorState);
  };

  const drawerProps = canSeeProposals
    ? {
        isOpen: isDrawerOpen,
        links,
        scenarioId: scenario,
        originId: origin,
        canSubmit,
        canReview,
        reviewLinkId: linkToReview,
        onClose: closeDrawer,
        onReviewLoaded: setHasPending,
        onOpenProposal: openProposal,
      }
    : null;

  return { access, drawerProps };
};
